import type { Org } from "@/api/orgs"
import type { User } from "@/api/users"
import { subscribeWhenReady } from "@/util/widgetReady"

export function loadIntercom(user: User, org: Org) {
  if (typeof Intercom !== "undefined") {
    Intercom("boot", {
      app_id: "jwmgbua5",
      email: user.email,
      created_at: user.createdAt as unknown as number,
      name: user.name,
      user_id: String(user.id),
      company: {
        company_id: org.subdomain,
        name: org.name,
        is_self_serve: org.isSelfServe,
      },
    })
  }
}

// Whether the real Intercom widget has booted. The inline loader (views/
// intercom.erb) sets `window.Intercom` to a command-queue stub immediately, so
// its mere presence does not mean the async widget script loaded; the stub has
// no `booted` flag, while the real widget sets `booted = true` after boot.
// Mirrors `isPylonLoaded` so support-chat resolution can confirm the expected
// provider actually loaded rather than detecting the stub.
export function isIntercomLoaded(): boolean {
  return typeof Intercom !== "undefined" && Intercom.booted === true
}

export function showNewIntercomMessage(message?: string): boolean {
  if (typeof Intercom !== "undefined") {
    Intercom("showNewMessage", message)
    return true
  } else {
    return false
  }
}

export function showIntercom(): boolean {
  if (typeof Intercom !== "undefined") {
    Intercom("show")
    return true
  }
  return false
}

export function hideIntercom(): boolean {
  if (typeof Intercom !== "undefined") {
    Intercom("hide")
    return true
  }
  return false
}

export interface IntercomVisibilityHandlers {
  onShow: () => void
  onHide: () => void
}

// Mirrors `subscribePylonVisibility`. Intercom has no API to remove a
// visibility callback, so cleanup replaces them with no-ops. When the async
// script hasn't set `Intercom` yet, registration is deferred until it appears
// so handlers still attach; the cleanup cancels that pending wait if it hasn't.
export function subscribeIntercomVisibility(
  handlers: IntercomVisibilityHandlers,
): () => void {
  return subscribeWhenReady(
    () => typeof Intercom !== "undefined",
    () => {
      Intercom("onShow", handlers.onShow)
      Intercom("onHide", handlers.onHide)
      return () => {
        if (typeof Intercom !== "undefined") {
          Intercom("onShow", () => {})
          Intercom("onHide", () => {})
        }
      }
    },
  )
}

// Mirrors `subscribePylonUnreadCount`. Intercom has no API to remove an
// unread-count callback, so cleanup replaces it with a no-op. When the async
// script hasn't set `Intercom` yet, registration is deferred until it appears
// so the handler still attaches; the cleanup cancels that pending wait if it
// hasn't.
export function subscribeIntercomUnreadCount(
  handler: (count: number) => void,
): () => void {
  return subscribeWhenReady(
    () => typeof Intercom !== "undefined",
    () => {
      Intercom("onUnreadCountChange", handler)
      return () => {
        if (typeof Intercom !== "undefined") {
          Intercom("onUnreadCountChange", () => {})
        }
      }
    },
  )
}
